import React, { useMemo } from "react"
import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import { Form, FormFeedback, Label, Button } from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import { useAccountAccountTypes } from "hooks/useAccountAccountType"
import {
  useCreateCarCareAccount,
  useEditCarCareAccount,
} from "hooks/useCarCareAccount"

const STATUS_OPTIONS = [
  { value: "1", label: "Active" },
  { value: "0", label: "Inactive" },
]

const DEFAULT_OPTIONS = [
  { value: "1", label: "Yes" },
  { value: "0", label: "No" },
]

const CarCareAccountAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateCarCareAccount()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditCarCareAccount()

  const { data: typesData } = useAccountAccountTypes({ all: true })

  const typeOptions = useMemo(() => {
    const list = typesData?.data?.data || typesData?.data || []
    return list.map(t => ({ value: t.id?.toString(), label: t.name }))
  }, [typesData])

  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Please enter the account name")
      .max(255, "Name cannot exceed 255 characters"),
    opening_balance: Yup.number()
      .typeError("Enter a valid amount")
      .nullable(),
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editedData?.name || "",
      account_type_id: editedData?.account_type_id?.toString() || "",
      opening_balance: editedData?.opening_balance ?? 0,
      currency: editedData?.currency || "BDT",
      is_default: editedData?.is_default ? "1" : "0",
      is_active:
        editedData?.is_active === undefined
          ? "1"
          : editedData.is_active
          ? "1"
          : "0",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          ...values,
          account_type_id: values.account_type_id || null,
          opening_balance:
            values.opening_balance === "" ? 0 : Number(values.opening_balance),
          is_default: values.is_default === "1",
          is_active: values.is_active === "1",
        }

        const action = isEdit
          ? editMutation({ id: editedData.id, ...payload })
          : createMutation(payload)

        const res = await action
        toast.success(
          res?.message ??
            `Account ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        console.error(error)
        toast.error(error?.response?.data?.message ?? "Failed to save account.")
      }
    },
  })

  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Account"}${loading ? "..." : ""}`
  }, [isEdit, editLoading, createLoading])

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Car Care Account" : "Add Car Care Account"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <div className="mb-3">
          <Label htmlFor="name">
            Name <span className="text-danger">*</span>
          </Label>
          <InputField
            type="text"
            name="name"
            id="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched.name && !!formik.errors.name}
          />
          {formik.touched.name && formik.errors.name && (
            <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
          )}
        </div>

        <FormikCreatableSelect
          name="account_type_id"
          label="Account Type"
          formik={formik}
          options={typeOptions}
          placeholder="Select type (optional)"
        />

        <div className="mb-3">
          <Label htmlFor="opening_balance">Opening Balance</Label>
          <InputField
            type="number"
            name="opening_balance"
            id="opening_balance"
            value={formik.values.opening_balance}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={
              formik.touched.opening_balance &&
              !!formik.errors.opening_balance
            }
          />
          {formik.touched.opening_balance &&
            formik.errors.opening_balance && (
              <FormFeedback type="invalid">
                {formik.errors.opening_balance}
              </FormFeedback>
            )}
        </div>

        <div className="mb-3">
          <Label htmlFor="currency">Currency</Label>
          <InputField
            type="text"
            name="currency"
            id="currency"
            value={formik.values.currency}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>

        <FormikCreatableSelect
          name="is_default"
          label="Default Account"
          formik={formik}
          options={DEFAULT_OPTIONS}
        />

        <FormikCreatableSelect
          name="is_active"
          label="Status"
          formik={formik}
          options={STATUS_OPTIONS}
        />

        <div className="text-end mt-3">
          <Button
            color="success"
            type="submit"
            disabled={editLoading || createLoading}
          >
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default CarCareAccountAddEdit
